/* eslint-disable no-restricted-imports */
import React, { ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useOpenDialog } from '../../routing/ModalRoute';
import { DIALOGS } from '../../routing/constants';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useTranslation } from '../../utils/useTranslation';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Typography } from '../typography/Typography';
import { CollectionCardSkeleton } from './CollectionCard';
import type { CollectionUI } from './types';

const GridRoot = styled(Stack)
    .withConfig({ displayName: 'GridRoot' })
    .attrs({
        spacing: 1,
        direction: 'row',
        flexWrap: 'wrap',
        p: { xs: 1, md: 0 },
    })``;

interface CollectionsGridProps extends FlexProps {
    isLoading: boolean;
    collections: CollectionUI[];
    renderItem: (index: number) => ReactElement;
    stubMessage?: string;
}

export function CollectionsGrid(props: CollectionsGridProps) {
    const { isLoading, collections, renderItem, stubMessage, ...other } = props;
    const t = useTranslation();
    const openDialog = useOpenDialog();

    const isCreateCollectionEnabled =
        String(useExperimentFlag('create_collection_from_plus_enabled')) === '1';

    const openCreateCollection = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    if (isLoading) {
        return (
            <GridRoot count={6} renderItem={(index) => <CollectionCardSkeleton key={index} />} />
        );
    }

    if (collections.length === 0) {
        return (
            <Flex
                px={4}
                py={6}
                overflow="hidden"
                direction="column"
                alignItems="center"
                justifyContent="center"
                borderRadius={{ sm: 3 }}
                background="var(--background-primary)"
                {...other}
            >
                {stubMessage ? (
                    <Typography variant="body" color="secondary" textAlign="center">
                        {t(stubMessage)}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            as="h3"
                            variant="title3"
                            weight="bold"
                            mb={1}
                            textAlign="center"
                        >
                            {t('В избранном ничего нет')}
                        </Typography>
                        <Typography variant="body" color="secondary" textAlign="center">
                            {t(
                                'В этом разделе будут находиться посты, которые вы добавите из ленты',
                            )}
                        </Typography>
                        <Box pt={3}>
                            {isCreateCollectionEnabled ? (
                                <Button onClick={openCreateCollection} size="m">
                                    {t('Создать коллекцию')}
                                </Button>
                            ) : (
                                <Button as={Link} to="/" size="m" variant="outlined">
                                    {t('Перейти в ленту')}
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Flex>
        );
    }

    return <GridRoot count={collections.length} renderItem={renderItem} />;
}
